import clsx from "clsx";
import { useState, ChangeEvent } from "react";
import { useTranslation } from 'react-i18next';
import { RenderEditCellProps } from "@bst/react-data-grid";
import { InputCell, ScrollArea } from "@bst/react-ui";
import * as Popover from "@radix-ui/react-popover";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ProjectDTO, ProjectThresholdDTO } from "../../../apiclients/bstinsights";
import { useProjectsForThresholds } from "../Hooks/useProjectsForThresholds";

export const ProjectNameInputCell = ({
  onCancelAddOverride,
  onSelectProject,
  ...props
}: RenderEditCellProps<ProjectThresholdDTO> & {
  onCancelAddOverride: () => void;
  onSelectProject: (projectId: ProjectDTO) => void;
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState<string>("");
  const { projects, isFetching, hasNextPage, fetchNextPage } =
    useProjectsForThresholds(value);

  const rowVirtualizer = useVirtualizer({
    count: projects.length,
    getScrollElement: () => document.querySelector(".scrollarea__viewport"),
    estimateSize: () => 56,
    overscan: 8,
  });

  const getPopoverContainer = () => {
    const nlmSettingsElem = document.querySelector(
      ".nlm_project_overrides-settings"
    );
    const bodyElem = document.querySelector("body");
    return (nlmSettingsElem ?? bodyElem) as HTMLElement | null;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const isAtBottom = ({
    currentTarget,
  }: React.UIEvent<HTMLDivElement>): boolean => {
    return (
      currentTarget.scrollTop + 56 >=
      currentTarget.scrollHeight - currentTarget.clientHeight
    );
  };
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!isFetching && hasNextPage && isAtBottom(event)) {
      fetchNextPage();
    }
  };

  const noProjects = projects.length === 0;

  return (
    <Popover.Root open={true} modal={true}>
      <Popover.Anchor>
        <InputCell
          {...props}
          className="project-override-input"
          data-testid="project-override-input"
          value={value}
          onChange={handleChange}
          onBlur={() => { }}
          onClose={() => { }}
        />
      </Popover.Anchor>
      <Popover.Portal container={getPopoverContainer()}>
        <Popover.Content
          className={clsx("project-override-popover-content", noProjects && "project-override-popover-content-empty")}
          align="start"
          sideOffset={8}
          collisionPadding={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          asChild
        >
          <ScrollArea
            className="project-override-popover-scrollarea"
            onScroll={handleScroll}
          >
            {noProjects && !isFetching ? (
              t('nlmsettings.noProjects')
            ) : (
              <ul
                data-testid="project-override-popover-list"
                style={{
                  position: "relative",
                  width: "100%",
                  height: `${rowVirtualizer.getTotalSize()}px`,
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualItem) => (
                  <li
                    key={virtualItem.key}
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      onSelectProject(projects[virtualItem.index]);
                    }}
                    className="project-override-popover-item"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: `${virtualItem.size}px`,
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    <span className="project-override-popover-item-title">
                      {projects[virtualItem.index].projectName}
                    </span>
                    <span className="project-override-popover-item-subtitle">
                      {projects[virtualItem.index].projectCode}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
